import { BOND_TYPES, ORDER_SIDES, MKTX_DEALER_LIST_MODES, MKTX_INQUIRY_TYPES, MKTX_PRICE_TYPES, MKTX_PRICING_PROCESSES, MKTX_STRATEGY_TYPES, PROTOCOL_TYPES, PROTOCOL_TO_DEALER_PARAM } from "../../Constants/AppConstants";
import { isTradingDeskVenueLabel } from "../../Constants/stagingBlotter";

export const bondTypes = {
    [BOND_TYPES.TREASURY]: {
      label: BOND_TYPES.TREASURY,
      fields: [
        "minimumRating",
        "couponRange",
        "maturityRanges",
        "duration",
        "price",
        "yield",
        "zeroCoupon",
        "callable",
        "securityCharge",
      ],
    },
    [BOND_TYPES.MUNICIPALS]: {
      label: BOND_TYPES.MUNICIPALS,
      fields: [
        "minimumRating",
        "minimumRatingMoody",
        "couponRange",
        "maturityRanges",
        "duration",
        "price",
        "yield",
        "zeroCoupon",
        "callable",
        "securityCharge",
        "state",
        "underlyingRating",
        "insured",
        // "call",
        "taxable",
        "paymentFrequency",
        "incomeSource",
        "altMinTax",
      ],
    },
    [BOND_TYPES.CORPORATES]: {
      label: BOND_TYPES.CORPORATES,
      fields: [
        "minimumRating",
        "minimumRatingMoody",
        "couponRange",
        "maturityRanges",
        "duration",
        "price",
        "yield",
        "zeroCoupon",
        "callable",
        "securityCharge",
        "insured",
        // "call",
        "paymentFrequency",
        "sector",
        "nextCallDate",
        "issuer", // Added issuer field
      ],
    },
  };

export const orderedBondTypeOptions = [
  BOND_TYPES.CORPORATES,
  BOND_TYPES.TREASURY,
  BOND_TYPES.MUNICIPALS,
];

const bondTypeOrderRank = {
  corp: 0,
  corporate: 0,
  corporates: 0,
  treas: 1,
  treasury: 1,
  treasuries: 1,
  gov: 1,
  government: 1,
  muni: 2,
  municipal: 2,
  municipals: 2,
};

export const orderBondTypeOptions = (options = []) => {
  if (!Array.isArray(options)) return [];

  return [...options].sort((a, b) => {
    const aRank = bondTypeOrderRank[String(a || "").trim().toLowerCase()] ?? 99;
    const bRank = bondTypeOrderRank[String(b || "").trim().toLowerCase()] ?? 99;
    return aRank - bRank;
  });
};

export const bondLadderTypeOptions = [
  ...orderedBondTypeOptions
];

export const fieldOptions = {
    venue: [], // Initialize with empty array, will be populated from API
    side: [ORDER_SIDES.BUY, ORDER_SIDES.SELL],
    bondType: [
      ...orderedBondTypeOptions
    ],
    callable: ["Yes", "No"],
    zeroCoupon: ["Yes", "No"],
    state: [],
    paymentFrequency: ["Monthly", "Quarterly", "Semi-Annual", "Annual"],
    sector: [
      "Technology",
      "Materials",
      "Industrial",
      "Financial",
      "Real Estate",
      "Healthcare",
      "Consumer Discretionary",
      "Utility",
      "Communication Services",
      "Consumer Staples",
      "Energy",
    ],
    insured: ["Yes", "No"],
    call: ["Yes", "No"],
    taxable: ["Yes", "No"],
    incomeSource: ["Tax-Exempt", "Taxable"],
    altMinTax: ["Yes", "No"],
    securityCharge: [],
    // MarketAxess specific options
    mktxDealerListMode: [
      MKTX_DEALER_LIST_MODES.INCLUDE_ONLY,
      MKTX_DEALER_LIST_MODES.INCLUDE_AT_LEAST,
    ],
    mktxInquiryType: [
      MKTX_INQUIRY_TYPES.ASAP,
      MKTX_INQUIRY_TYPES.BIN,
    ],
    mktxPriceType: [
      MKTX_PRICE_TYPES.PRICE,
      MKTX_PRICE_TYPES.SPREAD,
    ],
    // New: MarketAxess pricing process dropdown options
    mktxPricingProcess: [
      MKTX_PRICING_PROCESSES.ONESTEP,
      MKTX_PRICING_PROCESSES.DELAYEDSPOT,
    ],
    strategyType: [
      MKTX_STRATEGY_TYPES.PROVIDE,
      MKTX_STRATEGY_TYPES.SEEK,
      MKTX_STRATEGY_TYPES.TAKE,
    ],
  };

// Cache for venues to avoid redundant API calls
const venueCache = {};

// Cache for MarketAxess strategy parameter (>957 StrategyParametersGrp) definitions, keyed on
// strategy type - the same shape venueCache uses above, for the same reason: GET
// /strategy/parameters is keyed purely on strategy type (see fetchStrategyParameters's own
// signature, no bond or row argument at all), so every list row - or single order - sharing a
// type reuses one fetch instead of repeating it.
//
// Lives here, a plain module-level object rather than React state, because it has to be read
// from two places that do not share a component tree: ListRfqTab (to populate its Strategy
// Params dialog) and listRfqPayload.buildListRfqPayload (called from useOrderEntry, a plain
// function with no store/React access - see that file's own "no React and no store access"
// doc comment) to resolve each row's paramId/dataType at submit time. fieldOptions.venue
// above solves the identical problem for dealer options the same way.
const strategyParamDefsCache = {};

/**
 * Fetches (and caches) one strategy type's parameter definitions.
 *
 * @returns {Promise<Array>} whatever GET /strategy/parameters returned for this type, or []
 *          on any failure - callers show their own error state from the rejection, this only
 *          protects the cache from ever holding a partial/undefined result.
 */
export const loadStrategyParamDefs = async (token, strategyType) => {
  if (!strategyType) return [];
  if (strategyParamDefsCache[strategyType]) return strategyParamDefsCache[strategyType];

  const { fetchStrategyParameters } = await import('../../http/api');
  const defs = await fetchStrategyParameters(token, strategyType);
  strategyParamDefsCache[strategyType] = Array.isArray(defs) ? defs : [];
  return strategyParamDefsCache[strategyType];
};

/**
 * Whatever has been cached so far for a strategy type, read-only and synchronous - never
 * triggers a fetch. For buildListRfqPayload to resolve a row's strategyParams at submit time:
 * the list submit gate has loaded definitions for every populated row's strategy type,
 * including rows whose Strategy Params dialog was never opened. That gate also applies
 * server-provided defaults to parameters that have not been set on the row.
 */
export const getCachedStrategyParamDefs = (strategyType) => strategyParamDefsCache[strategyType] || [];

// Function to fetch venues from API and update fieldOptions
export const updateVenues = async (token, protocol) => {
  try {
    // Trumid RFQ: skip fetching, return specific option
    // if (protocol === PROTOCOL_TYPES.TRUMID_RFQ) {
    //     const trumidOptions = ["ALL"];
    //     fieldOptions.venue = trumidOptions;
    //     return trumidOptions;
    // }

    const param = PROTOCOL_TO_DEALER_PARAM[protocol] || "CLOB";
    
    // Check cache
    if (venueCache[param]) {
        fieldOptions.venue = venueCache[param];
        return fieldOptions.venue;
    }

    const { fetchDealers } = await import('../../http/api');
    const venuesData = await fetchDealers(token, param);
    
    let result = [];
    // Assuming the API returns an array of venue names or objects with name/value
    if (Array.isArray(venuesData)) {
      // If API returns venue objects with properties, extract the venue names
      if (venuesData.length > 0 && typeof venuesData[0] === 'object') {
        result = venuesData.map(venue => venue.dealerShortCode || venue.name || venue.venueName || venue.value);
      } else {
        // If API returns an array of venue names directly
        result = venuesData;
      }
    }
    
    // Update cache and fieldOptions
    venueCache[param] = result;
    fieldOptions.venue = result;
    return result;
  } catch (error) {
    console.error("Error fetching venues:", error);
    return [];
  }
};

// Sentinel the RFQ / Trumid dealer pickers use for "every dealer", never a real dealer name.
const ALL_DEALERS_OPTION = "ALL";

// Dealer ("venue") values arrive as an array (multi-select protocols) or a comma
// separated string (single select, and anything replayed from a stored order).
const normalizeVenueSelection = (venueValue) => {
  if (Array.isArray(venueValue)) {
    return venueValue.map((value) => String(value).trim()).filter((value) => value);
  }
  if (typeof venueValue === "string") {
    return venueValue.split(",").map((value) => value.trim()).filter((value) => value);
  }
  return [];
};

// The dealers a protocol may actually be traded with. Both the Dealer dropdown and the
// submit gate go through this, so what is offered and what is accepted cannot drift.
export const filterVenueOptions = ({ venues = [], protocol }) =>
  (venues || []).filter((option) => {
    if (protocol === PROTOCOL_TYPES.RFQ) {
      const optionLower = String(option).toLowerCase();
      return !(
        /ecn[12]/.test(optionLower) ||
        optionLower.includes("custodian") ||
        // not include schwab
        optionLower.includes("schwab") ||
        isTradingDeskVenueLabel(option)
      );
    }
    // Trading Desk is deliberately LEFT IN the send-to-market list. It is not a market
    // destination, but hiding it made the dropdown look inexplicably short and traders
    // read that as a bug. It is offered, selectable, and then refused at the submit gate
    // with an explanation — see isDealerSelectionBlocked / TRADING_DESK_SUBMIT_WARNING.
    return true;
  });

/**
 * Whether the current Dealer selection is the Trading Desk pseudo-venue.
 * Handles both the single-select string and the multi-select array shapes.
 */
export const isTradingDeskSelected = (venueValue) =>
  normalizeVenueSelection(venueValue).some(isTradingDeskVenueLabel);

/** Shown when a trader picks Trading Desk in the send-to-market modal. */
export const TRADING_DESK_SUBMIT_WARNING =
  "Trading Desk orders remain in the Staging Blotter. Select a dealer to send to market.";

// Selected dealers that the current protocol does not offer.
const getUnavailableVenues = (venueValue, options = []) => {
  const available = new Set((options || []).map((option) => String(option).trim()));
  return normalizeVenueSelection(venueValue).filter(
    (venue) => venue !== ALL_DEALERS_OPTION && !available.has(venue)
  );
};

// Mirrors the venue entry in useOrderEntry's requiredFields list.
const isDealerRequired = ({ protocol, strategyType, mktxDealerListMode }) => {
  if (protocol !== PROTOCOL_TYPES.MARKETAXESS) return true;
  if (
    strategyType === MKTX_STRATEGY_TYPES.SEEK ||
    strategyType === MKTX_STRATEGY_TYPES.PROVIDE
  ) {
    return false;
  }
  return mktxDealerListMode === MKTX_DEALER_LIST_MODES.INCLUDE_ONLY;
};

// Whether an order must be kept off the market because of its dealer selection.
// Prefilled dealers are free text — the staged order's own venue, or the bond's
// broker/dealer from secmaster — so they are regularly names we do not trade with. Such a
// value cannot be rendered by the Dealer dropdown, leaving the field visually empty while
// the order still carries it, which is how orders reached the market with a dealer nobody
// picked. Anything the dropdown cannot offer is therefore treated as no dealer at all.
export const isDealerSelectionBlocked = ({
  venue,
  options = [],
  optionsLoaded = true,
  protocol,
  strategyType,
  mktxDealerListMode,
}) => {
  // SEEK / PROVIDE hide the Dealer field altogether, so there is nothing the user could
  // correct — never block on a dealer they cannot see.
  if (
    protocol === PROTOCOL_TYPES.MARKETAXESS &&
    (strategyType === MKTX_STRATEGY_TYPES.SEEK || strategyType === MKTX_STRATEGY_TYPES.PROVIDE)
  ) {
    return false;
  }

  // Trading Desk is offered in the dropdown (see filterVenueOptions) rather than hidden,
  // because a silently shortened list reads as a bug. But it means "leave this in the
  // Staging Blotter", which is the one thing send-to-market cannot do — so refuse it and
  // let the caller show TRADING_DESK_SUBMIT_WARNING.
  if (isTradingDeskSelected(venue)) return true;

  // Deliberately NOT gated on the staging flag. An earlier version treated a still-staged
  // draft as having no dealer at all, on the grounds that its Dealer field only shows the
  // inventory dealer carried over rather than a chosen destination. That produced a
  // dead end: the flag only flips in handleVenueChange, so a trader who agreed with the
  // prefilled dealer — or re-picked the same one, which fires no change event — could
  // never enable the button. A prefilled dealer the desk actually trades with IS a valid
  // destination; the membership check below is what makes that judgement, and submit
  // stamps the order live (see useOrderEntry).

  const selected = normalizeVenueSelection(venue);

  if (selected.length === 0) {
    return isDealerRequired({ protocol, strategyType, mktxDealerListMode });
  }

  // Until the dealer list has loaded there is nothing to validate against; hold the button
  // rather than letting an unverified dealer through.
  if (!optionsLoaded || options.length === 0) return true;

  return getUnavailableVenues(selected, options).length > 0;
};

export const stateList = [
    { name: "Alabama", value: "AL" },
    { name: "Alaska", value: "AK" },
    { name: "Arizona", value: "AZ" },
    { name: "Arkansas", value: "AR" },
    { name: "California", value: "CA" },
    { name: "Colorado", value: "CO" },
    { name: "Connecticut", value: "CT" },
    { name: "Delaware", value: "DE" },
    { name: "Florida", value: "FL" },
    { name: "Georgia", value: "GA" },
    { name: "Hawaii", value: "HI" },
    { name: "Idaho", value: "ID" },
    { name: "Illinois", value: "IL" },
    { name: "Indiana", value: "IN" },
    { name: "Iowa", value: "IA" },
    { name: "Kansas", value: "KS" },
    { name: "Kentucky", value: "KY" },
    { name: "Louisiana", value: "LA" },
    { name: "Maine", value: "ME" },
    { name: "Maryland", value: "MD" },
    { name: "Massachusetts", value: "MA" },
    { name: "Michigan", value: "MI" },
    { name: "Minnesota", value: "MN" },
    { name: "Mississippi", value: "MS" },
    { name: "Missouri", value: "MO" },
    { name: "Montana", value: "MT" },
    { name: "Nebraska", value: "NE" },
    { name: "Nevada", value: "NV" },
    { name: "New Hampshire", value: "NH" },
    { name: "New Jersey", value: "NJ" },
    { name: "New Mexico", value: "NM" },
    { name: "New York", value: "NY" },
    { name: "North Carolina", value: "NC" },
    { name: "North Dakota", value: "ND" },
    { name: "Ohio", value: "OH" },
    { name: "Oklahoma", value: "OK" },
    { name: "Oregon", value: "OR" },
    { name: "Pennsylvania", value: "PA" },
    { name: "Rhode Island", value: "RI" },
    { name: "South Carolina", value: "SC" },
    { name: "South Dakota", value: "SD" },
    { name: "Tennessee", value: "TN" },
    { name: "Texas", value: "TX" },
    { name: "Utah", value: "UT" },
    { name: "Vermont", value: "VT" },
    { name: "Virginia", value: "VA" },
    { name: "Washington", value: "WA" },
    { name: "West Virginia", value: "WV" },
    { name: "Wisconsin", value: "WI" },
    { name: "Wyoming", value: "WY" }
  ];
